import type { TenantResolution } from "@/types";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

/**
 * Normalizes hostname by stripping port and lowercasing
 */
const normalizeHostname = (hostname: string): string => {
  return hostname.split(":")[0].toLowerCase();
};

/**
 * Resolves tenant slug from hostname and base domain.
 * Returns null for the global site (base domain or www).
 */
export const resolveTenantFromHost = (
  hostname: string,
  baseDomain: string,
): TenantResolution | null => {
  const host = normalizeHostname(hostname);
  const base = baseDomain.toLowerCase();

  if (LOCAL_HOSTS.has(host)) {
    const devSlug = import.meta.env.VITE_DEV_TENANT_SLUG;
    return devSlug ? { slug: devSlug } : null;
  }

  if (host === base || host === `www.${base}`) {
    return null;
  }

  const suffix = `.${base}`;
  if (host.endsWith(suffix)) {
    const slug = host.slice(0, -suffix.length);
    if (slug && slug !== "www") {
      return { slug };
    }
  }

  return null;
};

/**
 * Builds global site origin for redirects from tenant subdomain
 */
export const getGlobalSiteOrigin = (): string => {
  const { protocol, port } = window.location;
  const baseDomain =
    import.meta.env.VITE_BASE_DOMAIN ?? "tercerespacio.com.ar";
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${baseDomain}${portSuffix}`;
};

/**
 * Builds tenant site origin for manager redirects
 */
export const getTenantSiteOrigin = (slug: string): string => {
  const host = normalizeHostname(window.location.hostname);

  if (LOCAL_HOSTS.has(host)) {
    return window.location.origin;
  }

  const { protocol, port } = window.location;
  const baseDomain =
    import.meta.env.VITE_BASE_DOMAIN ?? "tercerespacio.com.ar";
  const portSuffix = port ? `:${port}` : "";

  return `${protocol}//${slug}.${baseDomain}${portSuffix}`;
};

/**
 * Builds the public tenant site URL for a space card CTA
 */
export const getSpaceTenantSiteUrl = (tenantSlug: string): string =>
  getTenantSiteOrigin(tenantSlug);
